import { useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Copy, Check, ArrowDownToLine } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const RewritePanel = ({ title, description, onUse, tone }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/portal/summary-rewrite", { title, description }); setRes(data); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(res.rewrite); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy — select the text and copy manually"); }
  };

  return (
    <div className="rounded-2xl border border-sky-400/20 bg-sky-400/5 p-4" data-testid="rewrite-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow !text-sky-300 flex items-center gap-2"><Wand2 size={13} /> Suggested rewrite</p>
          <p className="text-xs text-slate-400 mt-1">Drafts an improved version in short 2–3 sentence chunks using only what you've written. Timestamps and links below your summary are kept as-is. Check any [bracketed] placeholders before posting.</p>
        </div>
        <Button type="button" size="sm" onClick={run} disabled={busy} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="rewrite-button">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wand2 size={14} className="mr-1.5" />}{busy ? "Drafting…" : res ? "Try another" : "Suggest a rewrite"}
        </Button>
      </div>
      {res && (
        <div className="mt-3 space-y-3" data-testid="rewrite-result">
          <textarea readOnly value={res.rewrite} rows={7} className="w-full rounded-xl bg-white/5 border border-white/10 p-3 text-sm leading-relaxed text-slate-100 resize-y" data-testid="rewrite-text" />
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${tone(res.grade.score)}`} data-testid="rewrite-grade">Would grade <span className="font-display text-sm leading-none">{res.grade.grade}</span></span>
            <span className={`text-[11px] ${res.length < 600 ? "text-amber-300" : "text-slate-500"}`} data-testid="rewrite-length">{res.length} characters{res.extras_preserved ? " (summary only — your timestamps/links are kept below it)" : ""}{res.length < 600 ? " — add a detail or two to reach the 600 minimum" : ""}</span>
            <div className="ml-auto flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={copy} className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="rewrite-copy-button">{copied ? <Check size={14} className="mr-1.5 text-emerald-400" /> : <Copy size={14} className="mr-1.5" />}{copied ? "Copied" : "Copy"}</Button>
              {onUse && <Button type="button" size="sm" onClick={() => onUse(res.rewrite)} className="rounded-full bg-emerald-400 text-[#06251A] font-bold hover:bg-emerald-300" data-testid="rewrite-use-button"><ArrowDownToLine size={14} className="mr-1.5" /> Use this rewrite</Button>}
            </div>
          </div>
          {res.notes?.length > 0 && <ul className="text-xs text-slate-400 space-y-1" data-testid="rewrite-notes">{res.notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-sky-300">•</span><span>{n}</span></li>)}</ul>}
        </div>
      )}
    </div>
  );
};
